import { Box, Select, Stack } from "@chakra-ui/react";
import FormGrid from "@raiden/library/components/Form/FormGrid";
import InputPhone from "@raiden/library/components/Form/InputPhone";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import { COUNTRIES_STATE_VALUE_ENABLED } from "@raiden/library/constants/countries";
import {
  CUSTOMERS_GENDERS_LIST,
  CUSTOMERS_GENDERS_MESSAGE,
} from "@raiden/library/constants/customers";
import { useApi } from "@raiden/library/hooks/useApi";
import useLocale from "@raiden/library/hooks/useLocale";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import ApiErrorMessage from "../../../components/ApiErrorMessage";

function Form() {
  const intl = useIntl();

  const { locale } = useLocale();

  /** @type {import("react-hook-form").UseFormReturn<import("./Handler").WizardInformationFormValues>} */
  const { control } = useFormContext();

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Countries/Country").Country[]>} */
  const { swrResponse } = useApi(
    generateApiUri({
      id: "@countries.viewAny",
      query: {
        state: [COUNTRIES_STATE_VALUE_ENABLED],
        per_page: 250,
      },
    }),
  );

  const countries = useMemo(() => {
    return swrResponse.data?.data ?? [];
  }, [swrResponse.data?.data]);

  const enabledCountries = useMemo(() => {
    return countries.filter?.(
      (country) => country.state === COUNTRIES_STATE_VALUE_ENABLED,
    );
  }, [countries]);

  return (
    <Box>
      <ApiErrorMessage />

      <Stack spacing="1rem">
        <FormGrid>
          <FormController
            control={control}
            name="data.gender"
            label={intl.formatMessage({ defaultMessage: "Civilité" })}
            rules={{ required: true }}
            renderWithFormControl={(field) => (
              <Select {...field}>
                <option value="">-</option>

                {CUSTOMERS_GENDERS_LIST.map((gender, index) => (
                  <option value={gender} key={index}>
                    {intl.formatMessage(CUSTOMERS_GENDERS_MESSAGE, {
                      gender,
                    })}
                  </option>
                ))}
              </Select>
            )}
          />

          <FormController
            control={control}
            name="data.last_name"
            label={intl.formatMessage({ defaultMessage: "Nom" })}
            rules={{ required: true }}
          />

          <FormController
            control={control}
            name="data.first_name"
            label={intl.formatMessage({ defaultMessage: "Prénom" })}
            rules={{ required: true }}
          />
        </FormGrid>

        <FormGrid>
          <FormController
            control={control}
            name="data.birthdate"
            rules={{ required: true }}
            type="date"
            label={intl.formatMessage({
              defaultMessage: "Date de naissance",
            })}
          />
        </FormGrid>

        <FormGrid>
          <FormController
            name="data.address_1"
            rules={{ required: true }}
            label={intl.formatMessage({ defaultMessage: "Adresse" })}
          />

          <FormController
            name="data.address_2"
            label={intl.formatMessage({
              defaultMessage: "Complément d'adresse",
            })}
          />
        </FormGrid>

        <FormGrid>
          <FormController
            name="data.zipcode"
            rules={{ required: true }}
            label={intl.formatMessage({
              defaultMessage: "Code postal",
            })}
          />

          <FormController
            name="data.city"
            rules={{ required: true }}
            label={intl.formatMessage({
              defaultMessage: "Ville",
            })}
          />

          <FormController
            name="data.country_id"
            rules={{ required: true }}
            label={intl.formatMessage({
              defaultMessage: "Pays",
              description: "singular",
            })}
            renderWithFormControl={(fields) => (
              <Select {...fields} placeholder="-">
                {enabledCountries?.map((country, index) => (
                  <option value={country.id} key={country.id}>
                    {locale !== undefined && country.name?.[locale]}
                  </option>
                ))}
              </Select>
            )}
          />
        </FormGrid>

        <FormGrid>
          <InputPhone
            countries={countries}
            nameCountry="data.phone_country"
            nameNumber="data.phone_number"
          />
        </FormGrid>
      </Stack>
    </Box>
  );
}

export default Form;
